// ─────────────────────────────────────────────────────────────────────────────
// "WHY THIS?" (Ticket 6 #4).
//
// The profiled explanation is exactly two parts:
//
//   1. The why-string RETURNED BY THE RUNTIME, verbatim. It is a strict
//      projection of the components that actually scored the card
//      (`whyString()` over the scoring trace) — e.g.
//        "matches your interests: rugby, coaching · active in your life: the book"
//      We do not rewrite, prettify, re-case, or re-order it. Editorialising the
//      trace is how a why-string stops being true.
//
//   2. The FIXED blind line, identical on every card.
//
// There is no third part and no fallback prose. If the runtime returns an empty
// why (it does not — `whyString` always yields at least "no profile signal"),
// we render only the blind line rather than inventing a reason.
//
// COPY NOTE. The mock (`btbshowspacemock.html`) is the authority for the blind
// line and words it as "Computed by your agent, on your side. Between Two Beers
// never sees your profile." That sentence CONTAINS the exact fixed line the
// ticket specifies, so both are satisfied at once. PRIVACY_LINE below is kept
// as the ticket's literal sentence — separate from the mock's lead-in — so the
// required string stays independently assertable in test.
//
// The mock also shows a "why this?" on ANONYMOUS cards. That is not a profile
// leak: no profile exists in that mode, and the anonymous blind line says so.
// Any anonymous explanation must therefore be publisher-authored and identical
// for every visitor — see `editorialNote` on the card, which is optional and
// absent from the generated set, in which case only the blind line renders.
// ─────────────────────────────────────────────────────────────────────────────

import {SHOW_NAME} from './config'

/** The ticket's fixed line. Rendered as the tail of the profiled blind line. */
export const PRIVACY_LINE = `${SHOW_NAME} never sees your profile.`

/** The mock's lead-in, which precedes PRIVACY_LINE on a profiled card. */
export const PROFILED_BLIND_LEAD = 'Computed by your agent, on your side.'

/** The mock's blind line for the anonymous shelf — no profile is in play. */
export const ANON_BLIND_LINE = 'No profile in play — this shelf is the hosts’ own pour.'

/** The full profiled blind line, as the mock words it. */
export const PROFILED_BLIND_LINE = `${PROFILED_BLIND_LEAD} ${PRIVACY_LINE}`

export interface WhyThis {
  /** The runtime's trace projection, passed through untouched. */
  trace: string | null
  /** The fixed blind line for this mode. */
  blind: string
}

/**
 * Compose the "why this?" for a card.
 *
 * @param why       the `order[].why` string from POST /app/rank-candidates.
 *                  Ignored when `profiled` is false — an anonymous card has no
 *                  ranking and must never borrow one.
 * @param profiled  which shelf this card is on.
 */
export function whyThis(why: string | null | undefined, {profiled = true} = {}): WhyThis {
  if (!profiled) return {trace: null, blind: ANON_BLIND_LINE}
  const trace = typeof why === 'string' && why.trim() ? why.trim() : null
  return {trace, blind: PROFILED_BLIND_LINE}
}
